import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';
import { AvatarImage } from '@/components/ResponsiveImages';
import { generateInitials, hasValidAvatar, getImageSizes } from './constants';

const avatar = tv({
  base: 'border relative grid content-center items-center justify-center overflow-hidden rounded-full border-soft bg-p-white-170 ',
  variants: {
    size: {
      micro: 'size-x2 text-micro',
      xs: 'size-6 text-xs',
      sm: 'size-8 text-sm',
      md: 'size-12 text-base',
      lg: 'size-14 text-lg',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

type AvatarProps = VariantProps<typeof avatar>;

type Props = {
  owner: {
    displayName: string | null | undefined;
    imageUrl: string | null | undefined;
  };
  className?: string;
  priority?: boolean;
} & AvatarProps;

export const NarrativeAuthorAvatar = ({
  owner,
  size = 'sm',
  className,
  priority = false,
}: Props) => {
  const { displayName, imageUrl } = owner;
  const imageSizes = getImageSizes(size);
  const {
    mobile: mobileSize,
    tablet: tabletSize,
    desktop: desktopSize,
  } = imageSizes;

  const initials = generateInitials(displayName);
  const hasAvatar = hasValidAvatar(imageUrl);

  return (
    <div className={avatar({ size, className })}>
      <div className="text-center text-solid">{initials}</div>
      {hasAvatar && imageUrl && (
        <AvatarImage
          src={imageUrl}
          alt={displayName || 'User avatar'}
          mobileSize={mobileSize}
          tabletSize={tabletSize}
          desktopSize={desktopSize}
          priority={priority}
        />
      )}
    </div>
  );
};
